import { TRPCClientError } from '@trpc/client';
import { getTrpcErrorCode } from '@/shared/lib';

/**
 * 사용자가 결제창을 닫으면 토스 SDK v2 가 던지는 오류 코드(PublicError.Widgets.UserCancelError).
 * npm 타입(@tosspayments/tosspayments-sdk)에는 오류 이름만 있고, 코드 문자열은 SDK 본 스크립트
 * (https://js.tosspayments.com/v2/standard)의 오류 정의 `["취소되었습니다.",{code:"USER_CANCEL"}]` 에 있다.
 */
const USER_CANCEL_CODE = 'USER_CANCEL';

/** 토스 SDK 로더가 붙이는 개발자용 영어 문구 머리(스크립트 로드 실패 등) — 고객에게 그대로 보여 주지 않는다. */
const SDK_TECHNICAL_PREFIX = '[TossPayments SDK]';

const FALLBACK_MESSAGE = '결제를 시작하지 못했습니다. 다시 시도해 주세요.';
/** 결제 준비(payments.prepare)가 서버 답 없이 끝났을 때. */
const PREPARE_FAILED_MESSAGE = '결제 정보를 불러오지 못했습니다. 잠시 뒤 다시 시도해 주세요.';

export interface PaymentRequestError {
  /** 고객에게 보여 줄 문구. null 이면 안내하지 않는다. */
  readonly message: string | null;
  /** 원문이 개발자용(SDK 기술 문구·통신 오류·문구 없음)이라 콘솔에 남겨야 하는가. */
  readonly shouldLog: boolean;
}

const isUserCancel = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'code' in error && error.code === USER_CANCEL_CODE;

/** 입력 검증 거절(BAD_REQUEST)이면 api-contracts 가 data.fieldErrors 에 필드별 오류를 싣는다 — 그 밖에는 null. */
const hasFieldErrors = (error: TRPCClientError<never>): boolean => {
  const data: unknown = error.data;
  return (
    typeof data === 'object' &&
    data !== null &&
    'fieldErrors' in data &&
    data.fieldErrors !== null &&
    data.fieldErrors !== undefined
  );
};

/**
 * 우리 서버가 답한 오류의 문구 — tRPC 오류 본문(data.code)이 있을 때만이다. 서버 문구는 고객용 한글 안내다
 * (도메인 거절, INTERNAL 은 api-contracts 가 가림 문구로 바꾼다).
 * 응답을 받지 못했거나 tRPC 오류 본문이 아닌 응답이면(Failed to fetch·NetworkError…·Load failed·
 * Unable to transform response…) 브라우저·링크의 영어 원문이라 null. 입력 검증 거절(fieldErrors 있음)도 문구가
 * 검증기(Zod)의 기술 원문이라 null.
 */
const serverReplyMessage = (error: unknown): string | null =>
  error instanceof TRPCClientError &&
  getTrpcErrorCode(error) !== null &&
  !hasFieldErrors(error) &&
  error.message
    ? error.message
    : null;

/** 결제 화면이 부르는 우리 API 오류의 고객 문구 — 서버가 답한 안내는 그대로, 통신 오류 원문은 fallback 으로 바꾼다. */
export const paymentApiErrorMessage = (error: unknown, fallback: string): string =>
  serverReplyMessage(error) ?? fallback;

/**
 * 결제창 요청(결제 준비 → requestPayment) 실패를 고객 안내로 바꾼다.
 * - 결제 준비(우리 API, TRPCClientError): 서버가 답한 안내는 그대로, 통신 오류는 결제 정보 문구를 보이고 원문은 콘솔에 남긴다.
 * - 사용자가 결제창을 닫음(USER_CANCEL): 스스로 그만둔 것이라 안내하지 않는다 — 결제 버튼은 그대로 다시 누를 수 있다.
 * - SDK 기술 문구(`[TossPayments SDK] …`)나 문구 없음: 우리 문구를 보여 주고 원문은 콘솔에 남긴다.
 * - 그 밖(토스가 준 한글 안내): 그 문구를 그대로 보여 준다.
 */
export const describePaymentRequestError = (error: unknown): PaymentRequestError => {
  if (error instanceof TRPCClientError) {
    const serverMessage = serverReplyMessage(error);

    return serverMessage !== null
      ? { message: serverMessage, shouldLog: false }
      : { message: PREPARE_FAILED_MESSAGE, shouldLog: true };
  }

  if (isUserCancel(error)) {
    return { message: null, shouldLog: false };
  }

  if (
    !(error instanceof Error) ||
    !error.message ||
    error.message.startsWith(SDK_TECHNICAL_PREFIX)
  ) {
    return { message: FALLBACK_MESSAGE, shouldLog: true };
  }

  return { message: error.message, shouldLog: false };
};
